import React, { useEffect, ReactNode, useState, useMemo } from "react";

import {
    RegUIClientProvider,
    FullRegUISessionDetails,
    useRegUIClient
} from "@foundation/reg-ui-client";
import MediaServicesSearchClient from "@foundation/media-services-search-client";
import { AbbaProvider } from "@foundation/abba-client";
import { isJapanesePage } from "@foundation/util";
import { PersonalizationClient } from "@foundation/personalization";

import { NAV_ENVIRONMENT, User } from "./types";
import { Locale } from "./constants";
import { NavContext, DEFAULT_CONTEXT_PROPS } from "./NavContext";
import NavSignInClient from "./NavSignInClient";

type NavContextProviderProps = {
    children: ReactNode;
    environment?: NAV_ENVIRONMENT;
    menuItems?: Record<string, any>;
    abbaPlatformObject?: string;
    user?: User;
    locale?: Locale;
};

type WithRegionProps = {
    children: ReactNode;
    user?: User;
    navSignInClient: NavSignInClient;
    locale?: Locale;
};

type WithAbbaProps = {
    children: ReactNode;
    abbaPlatformObject?: string;
    navSignInClient: NavSignInClient;
    locale?: Locale;
};

const NAV_BAR = "nav-bar";

function getEffectiveLocale(locale?: Locale): Locale {
    if (locale === Locale.en || locale === Locale.ja) {
        return locale;
    }
    if (typeof window !== "undefined") {
        return isJapanesePage(new URL(window.location.href)) ? Locale.ja : Locale.en;
    }

    return Locale.en;
}

function localizeUrl(locale: Locale, url: string): string {
    if (locale === Locale.ja) {
        return url.replace("bloomberg.com/", "bloomberg.com/jp/");
    }

    return url;
}

const WithRegion = ({ children, user, navSignInClient, locale }: WithRegionProps) => {
    const searchClient = useMemo(() => new MediaServicesSearchClient(), []);
    const [personalizationClient, setPersonalizationClient] = useState<PersonalizationClient | undefined>();

    useEffect(() => {
        setPersonalizationClient(new PersonalizationClient({
            application: "nav",
            clientUrl: process.env.NEXT_PUBLIC_PERSONALIZATION_URL || ""
        }));
    }, []);

    const effectiveLocale = getEffectiveLocale(locale);
    const subscriptionUrl = localizeUrl(effectiveLocale, DEFAULT_CONTEXT_PROPS.subscriptionUrl);
    const upgradeUrl = localizeUrl(effectiveLocale, DEFAULT_CONTEXT_PROPS.upgradeUrl);

    const navContext = useMemo(() => ({
        user,
        searchClient,
        personalizationClient,
        initializeSignInWithRegUI: navSignInClient.setSignInButton,
        onSignOut: navSignInClient.onSignOut,
        subscriptionUrl,
        upgradeUrl,
    }), [user, searchClient, navSignInClient, personalizationClient, subscriptionUrl, upgradeUrl]);

    return (
        <NavContext.Provider value={navContext}>
            {children}
        </NavContext.Provider>
    );
};

export const WithAbba = ({
    abbaPlatformObject = NAV_BAR,
    children,
    navSignInClient,
    locale
}: WithAbbaProps) => {
    const { session = {}, client: regUIClient } = useRegUIClient();
    const {
        user,
        isLoggedIn,
        subscriptionsData,
        entitlementFeatures,
    } = (session as FullRegUISessionDetails);
    const isSubscriber = Boolean(subscriptionsData?.length && subscriptionsData.length > 0);
    const showUpgrade = entitlementFeatures?.length > 0;

    const navUser = useMemo(() => ({
        isLoggedIn,
        isSubscriber,
        firstName: user?.firstName,
        showUpgrade,
        subscriptionsData
    }), [session]);

    return (
        <AbbaProvider platform={abbaPlatformObject} user={regUIClient?.fetchAbbaUserInfo()}>
            <WithRegion
                children={children}
                user={navUser}
                navSignInClient={navSignInClient}
                locale={locale}
            />
        </AbbaProvider>
    );
};

export const NavProvider = ({
    children,
    abbaPlatformObject = NAV_BAR,
    locale,
}: NavContextProviderProps) => {
    const navSignInClient = useMemo(() => new NavSignInClient(), []);

    return (
        <RegUIClientProvider onUserInfoReady={navSignInClient.setOnUserInfoReady}>
            <WithAbba
                abbaPlatformObject={abbaPlatformObject}
                children={children}
                navSignInClient={navSignInClient}
                locale={locale}
            />
        </RegUIClientProvider>
    );
};
